import React from "react";
import { Box, Typography } from "@mui/material";
import {
	PictureAsPdf as PdfIcon,
	Description as DocIcon,
	TableChart as SpreadsheetIcon,
	InsertDriveFile as FileIcon,
	Image as ImageIcon,
	CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

function getDocIcon(mimeType, size = 22) {
	if (!mimeType) return <FileIcon sx={{ fontSize: size, color: "#9E9E9E" }} />;
	if (mimeType.startsWith("image/")) return <ImageIcon sx={{ fontSize: size, color: "#7C4DFF" }} />;
	if (mimeType === "application/pdf") return <PdfIcon sx={{ fontSize: size, color: "#F44336" }} />;
	if (mimeType.includes("spreadsheet") || mimeType === "text/csv")
		return <SpreadsheetIcon sx={{ fontSize: size, color: "#4CAF50" }} />;
	if (mimeType.includes("wordprocessingml") || mimeType === "text/plain" || mimeType === "text/markdown")
		return <DocIcon sx={{ fontSize: size, color: "#42A5F5" }} />;
	return <FileIcon sx={{ fontSize: size, color: "#9E9E9E" }} />;
}

function ImagePreview({ file }) {
	const blobUrl = file.previewUrl;

	if (!blobUrl) return <SafeFileChip file={file} />;

	return (
		<Box
			component="img"
			src={blobUrl}
			alt={file.originalName}
			sx={{
				maxWidth: "200px",
				maxHeight: "150px",
				borderRadius: "8px",
				objectFit: "cover",
				border: "1px solid #E0E0E0",
			}}
		/>
	);
}

function SafeFileChip({ file }) {
	const url = file.presignedUrl;
	const Wrapper = url ? "a" : "div";
	const wrapperProps = url
		? { href: url, target: "_blank", rel: "noopener noreferrer", download: file.originalName }
		: {};

	return (
		<Wrapper {...wrapperProps} style={{ textDecoration: "none", color: "inherit" }}>
			<Box
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: 0.75,
					height: 32,
					px: 1.25,
					borderRadius: "10px",
					backgroundColor: "#F5F5F5",
					border: "1px solid #E0E0E0",
					cursor: url ? "pointer" : "default",
					transition: "background-color 0.15s",
					"&:hover": url ? { backgroundColor: "#EEEEEE" } : {},
					maxWidth: 220,
				}}
			>
				{getDocIcon(file.mimeType, 18)}
				<Typography
					sx={{
						fontSize: "12px",
						fontWeight: 500,
						color: "#212121",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						lineHeight: 1.3,
						maxWidth: 150,
					}}
				>
					{file.originalName || "File"}
				</Typography>
				<CheckCircleIcon sx={{ fontSize: 14, color: "#4CAF50", flexShrink: 0 }} />
			</Box>
		</Wrapper>
	);
}

function DocumentPreview({ file }) {
	return <SafeFileChip file={file} />;
}

export default function FileInlinePreview({ files }) {
	if (!files || files.length === 0) return null;

	return (
		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, marginTop: "6px", alignSelf: "flex-end", justifyContent: "flex-end" }}>
			{files.map((file, idx) => {
				const isImage = file.mimeType?.startsWith("image/");
				return (
					<Box key={file.fileId || idx}>
						{isImage ? <ImagePreview file={file} /> : <DocumentPreview file={file} />}
					</Box>
				);
			})}
		</Box>
	);
}
